import React, { useEffect, useState } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, Pagination, useTheme, IconButton, Tooltip,
  TextField, InputAdornment, useMediaQuery
} from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import StopIcon from '@mui/icons-material/Stop';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import { stopWorkflowRun, getWorkflowRuns } from '../../redux/Slice/Workflow/workflowRunsSlice';

const stateChip = (state, isXs, isSm) => {
  const chipSize = isXs || isSm ? 'small' : 'medium';
  const chipSx = {
    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
    height: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '28px' },
    '& .MuiChip-icon': {
      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' }
    }
  };
  
  switch (state?.toLowerCase()) {
    case "completed":
      return <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size={chipSize} sx={chipSx} />;
    case "cancelled":
      return <Chip icon={<CancelIcon />} label="Cancelled" color="error" size={chipSize} sx={chipSx} />;
    case "in progress":
      return <Chip icon={<AutorenewIcon />} label="In progress" color="info" size={chipSize} sx={chipSx} />;
    case "active":
      return <Chip icon={<AutorenewIcon />} label="Active" color="primary" size={chipSize} sx={chipSx} />;
    case "abandoned":
      return <Chip icon={<CancelIcon />} label="Abandoned" color="warning" size={chipSize} sx={chipSx} />;
    default:
      return <Chip label={state} size={chipSize} sx={chipSx} />;
  }
};


const formatDate = (date) => dayjs(date).format("D MMM YY [at] h:mma");

const PAGE_SIZE = 10;

const RunsTab = ({ workflowId }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const dispatch = useDispatch();
  const { runs, pagination, loading, stopLoading } = useSelector(state => state.workflowRuns);
  const [startDate, setStartDate] = useState(dayjs().subtract(20, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (workflowId) {
      console.log('Dispatching getWorkflowRuns with search:', debouncedSearchQuery);
      dispatch(getWorkflowRuns({
        workflowId,
        startTime: startDate.startOf("day").toISOString(),
        endTime: endDate.endOf("day").toISOString(),
        page: currentPage,
        limit: PAGE_SIZE,
        search: debouncedSearchQuery
      }));
    }
  }, [workflowId, startDate, endDate, currentPage, debouncedSearchQuery, dispatch]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Handle stopping a workflow run
  const handleStopRun = async (enrollmentId) => {
    dispatch(stopWorkflowRun({ workflowId, enrollmentId }));
  };

  const runsDatePickerSlotProps = {
    textField: {
      fullWidth: isXs,
      size: isXs || isSm ? "small" : "medium",
      sx: {
        minWidth: { xs: 0 },
        "& .MuiOutlinedInput-root": {
          fontSize: { xs: "0.72rem", sm: "0.85rem", md: "0.9rem", lg: "0.95rem", xl: "1rem" },
          minHeight: { xs: 34 },
        },
        "& .MuiOutlinedInput-input": {
          py: { xs: "5px" },
        },
        "& .MuiInputLabel-root": {
          fontSize: { xs: "0.74rem", sm: "0.875rem" },
        },
      },
    },
  };

  return (
    <Box sx={{ 
      p: { xs: 0.5, sm: 1, md: 2, lg: 2.5, xl: 3 },
      height: '100%',
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: "space-between", 
        mb: { xs: 0.5, sm: 1.25, md: 2, lg: 2, xl: 2 }, 
        gap: { xs: 1.5, sm: 1, md: 1.5, lg: 2, xl: 2 }, 
        alignItems: { xs: 'stretch', sm: 'center' },
        flexShrink: 0
      }}>
        <TextField
          placeholder="Search by contact name or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size={isXs || isSm ? 'small' : 'medium'}
          sx={{ 
            minWidth: { xs: '100%', sm: 250, md: 280, lg: 300, xl: 300 },
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem', xl: '1rem' }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.1rem', lg: '1.25rem', xl: '1.25rem' } }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  edge="end"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{
          display: "flex",
          gap: { xs: 0.5, sm: 1.5, md: 1.5, lg: 2, xl: 2 },
          flexDirection: { xs: 'row', sm: 'row' },
          minWidth: 0,
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start date"
              value={startDate}
              maxDate={endDate}
              onChange={v => v && setStartDate(v)}
              sx={isXs ? { flex: 1, minWidth: 0 } : undefined}
              slotProps={runsDatePickerSlotProps}
            />
            <DatePicker
              label="End date"
              value={endDate}
              minDate={startDate}
              maxDate={dayjs()}
              onChange={v => v && setEndDate(v)}
              sx={isXs ? { flex: 1, minWidth: 0 } : undefined}
              slotProps={runsDatePickerSlotProps}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(isXs ? { flex: '1 1 0%' } : {}),
      }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          mb: { xs: 0, sm: 1.25, md: 2, lg: 2, xl: 2 },
          ...(isXs ? { flex: '1 1 0%' } : {}),
          scrollbarWidth: { xs: 'none', sm: 'none', md: 'thin', lg: 'thin', xl: 'thin' },
          msOverflowStyle: { xs: 'none', sm: 'none', md: 'auto', lg: 'auto', xl: 'auto' },
          '&::-webkit-scrollbar': {
            width: { xs: 0, sm: 0, md: '8px', lg: '10px', xl: '10px' },
            height: { xs: 0, sm: 0, md: '8px', lg: '10px', xl: '10px' },
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Conversation
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                State
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Started on
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Stop
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  <Box>
                    <Typography 
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' } }}
                    >
                      {run.contactName}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' } }}
                    >
                      {run.contactPhone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  {stateChip(run.state, isXs, isSm)}
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  {formatDate(run.startedAt)}
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  <Tooltip title="Stop workflow run">
                    <IconButton 
                      size={isXs || isSm ? 'small' : 'medium'}
                      color="error"
                      onClick={() => handleStopRun(run._id)}
                      disabled={stopLoading}
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }}
                    >
                      <StopIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && !loading && (
              <TableRow>
                <TableCell 
                  colSpan={4} 
                  align="center"
                  sx={{ 
                    padding: { xs: '16px 8px', sm: '20px 10px', md: '24px 12px', lg: '28px 16px', xl: '32px 16px' }
                  }}
                >
                  <Typography 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' } }}
                  >
                    No runs found for this range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: { xs: 0.5, sm: 0 },
        px: { xs: 0, sm: 1, md: 1.5, lg: 2, xl: 2 },
        flexShrink: 0,
        pt: { xs: 0.5, sm: 0, md: 0, lg: 0, xl: 0 },
        mt: { xs: 'auto', sm: 0, md: 0, lg: 0, xl: 0 },
        mb: { xs: 0, sm: 2, md: 0, lg: 0, xl: 0 },
        minWidth: 0,
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' },
            minWidth: 0,
            flexShrink: 1,
            ...(isXs && {
              pr: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
          }}
        >
          {runs.length > 0 && (
            <>
              {`${(pagination.currentPage - 1) * PAGE_SIZE + 1}-${(pagination.currentPage - 1) * PAGE_SIZE + runs.length}`} of {pagination.totalRuns} items
            </>
          )}
        </Typography>
        <Pagination
          count={pagination.totalPages}
          page={pagination.currentPage}
          onChange={handlePageChange}
          color="primary"
          size={isXs || isSm ? 'small' : 'medium'}
          sx={{
            flexShrink: 0,
            '& .MuiPaginationItem-root': {
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.9rem' }
            }
          }}
        />
      </Box>
      </Box>
    </Box>
  );
};

export default RunsTab; 